import type { Board, BoardEdge } from '../protocol/ui';
import type { BoardVisual } from './types';
import { edgeGeometry } from './edgeGeometry';

/** Public display positions only. Never infers ownership, legality or graph adjacency. */
export function edgePieceSlots(board: Board, edge: BoardEdge, visual: BoardVisual, offset = 0): { x: number; y: number; angle: number; width: number; height: number }[] {
  const style = visual.edgePieces;
  if (!style) return [];
  const count = edge.segments ?? 1;
  if (!Number.isInteger(count) || count < 1 || count > 16) return [];
  const explicit = visual.placements?.find(p => p.edge === edge.id);
  if (explicit) return explicit.slots.length === count ? explicit.slots.map(s => ({ ...s, width: s.width ?? style.width, height: s.height ?? style.height })) : [];
  // Each disconnected part contributes its own measured segments: wrap gaps never become rails.
  const segments = edgeGeometry(board, edge, visual, offset).parts.flatMap(part => part.slice(1).map((b, i) => {
    const a = part[i]!; return { a, b, length: Math.hypot(b[0] - a[0], b[1] - a[1]) };
  })).filter(s => s.length > 0);
  const total = segments.reduce((n, s) => n + s.length, 0);
  if (!total) return [];
  return Array.from({ length: count }, (_, i) => {
    let distance = total * (i + .5) / count;
    let segment = segments.at(-1)!;
    for (const s of segments) { if (distance <= s.length) { segment = s; break; } distance -= s.length; }
    const t = Math.min(1, distance / segment.length), { a, b } = segment;
    return { x: a[0] + (b[0] - a[0]) * t, y: a[1] + (b[1] - a[1]) * t, angle: Math.atan2(b[1] - a[1], b[0] - a[0]) * 180 / Math.PI, width: style.width, height: style.height };
  });
}
